import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Check, Wrench } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PROPERTIES = [
  ["de-la-vie", "De La Vie Maison"],
  ["heritage", "Heritage"],
  ["hearthstead", "Hearthstead Cottage"],
];
const CATEGORIES = [
  "Plumbing",
  "Heating & AC",
  "Electrical",
  "Appliance",
  "Internet / Wi-Fi",
  "Other",
];
const URGENCY = [
  ["Emergency", "Emergency — needs attention today"],
  ["Soon", "Soon — this week"],
  ["Flexible", "Whenever convenient"],
];

export default function MaintenancePage() {
  const [form, setForm] = useState({
    name: "",
    property: "de-la-vie",
    category: "Plumbing",
    urgency: "Soon",
    contact: "",
    description: "",
  });
  const [photo, setPhoto] = useState(null);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const update = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const body = new FormData();
      body.append("name", form.name);
      body.append("property", form.property);
      body.append("category", form.category);
      body.append("urgency", form.urgency);
      body.append("description", form.description);
      if (form.contact) body.append("contact", form.contact);
      if (photo) body.append("photo", photo);
      await axios.post(`${API}/maintenance`, body);
      setSent(true);
    } catch {
      toast.error("Something went wrong sending your request. Please call us at 225-431-5052.");
    } finally {
      setSending(false);
    }
  };

  const inputCls =
    "w-full bg-transparent border-b border-[#16301f]/25 focus:border-[#16301f] outline-none py-2.5 text-sm text-[#16301f] placeholder:text-[#16301f]/35 transition-colors duration-300";
  const labelCls = "block text-[10px] uppercase tracking-[0.25em] text-[#16301f]/60 mb-1";

  return (
    <div data-testid="maintenance-page" className="min-h-screen bg-[#f7f2e7] text-[#16301f]">
      <div className="max-w-xl mx-auto px-5 py-12 sm:py-16">
        <div className="text-center mb-10">
          <img src="/images/logo.png" alt="Parker Furnished Stays seal" className="w-20 h-20 mx-auto rounded-full" />
          <p className="text-xs uppercase tracking-[0.3em] text-[#16301f]/60 mt-6 flex items-center justify-center gap-4">
            <span className="h-px w-10 bg-[#16301f]/40 inline-block" />
            Guest Services
            <span className="h-px w-10 bg-[#16301f]/40 inline-block" />
          </p>
          <h1 className="font-display font-medium text-4xl sm:text-5xl mt-5">Maintenance request</h1>
          <p className="font-script text-3xl text-[#1a3324] mt-3 -rotate-1 inline-block">
            Something needs attention? We're on it.
          </p>
          <p className="text-sm text-[#16301f]/70 font-light mt-6 max-w-md mx-auto">
            Tell us what's going on and we'll take care of it. For anything urgent,
            you can also call us directly at{" "}
            <a href="tel:+12254315052" className="underline underline-offset-4 decoration-[#16301f]/30 hover:text-[#16301f]">
              225-431-5052
            </a>
            .
          </p>
        </div>

        <div className="border border-[#16301f]/15 bg-[#fbf8f0] p-6 sm:p-10">
          {sent ? (
            <div data-testid="maintenance-success" className="text-center py-12">
              <span className="mx-auto w-14 h-14 rounded-full border border-[#16301f] flex items-center justify-center">
                <Check className="w-6 h-6 text-[#16301f]" />
              </span>
              <h2 className="font-display text-3xl mt-6">Request received</h2>
              <p className="text-sm text-[#16301f]/70 font-light mt-3 max-w-sm mx-auto">
                Thank you{form.name ? `, ${form.name.split(" ")[0]}` : ""} — your maintenance
                request has been sent to our team and we'll be in touch shortly.
              </p>
              <a
                data-testid="maintenance-back-home"
                href="/"
                className="inline-block mt-8 text-xs uppercase tracking-[0.22em] text-[#16301f]/70 border-b border-[#16301f]/40 pb-1 hover:text-[#16301f] hover:border-[#16301f] transition-colors duration-300"
              >
                Back to website
              </a>
            </div>
          ) : (
            <form data-testid="maintenance-form" onSubmit={handleSubmit} className="space-y-7">
              <div>
                <label htmlFor="mx-name" className={labelCls}>Your Name *</label>
                <input id="mx-name" data-testid="maintenance-name-input" required value={form.name} onChange={update("name")} placeholder="Your name" className={inputCls} />
              </div>
              <div className="grid sm:grid-cols-2 gap-x-8 gap-y-7">
                <div>
                  <label htmlFor="mx-property" className={labelCls}>Residence</label>
                  <select id="mx-property" data-testid="maintenance-property-select" value={form.property} onChange={update("property")} className={`${inputCls} cursor-pointer`}>
                    {PROPERTIES.map(([id, name]) => (
                      <option key={id} value={id}>{name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label htmlFor="mx-category" className={labelCls}>What's affected?</label>
                  <select id="mx-category" data-testid="maintenance-category-select" value={form.category} onChange={update("category")} className={`${inputCls} cursor-pointer`}>
                    {CATEGORIES.map((c) => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div>
                <label htmlFor="mx-urgency" className={labelCls}>How urgent is it?</label>
                <select id="mx-urgency" data-testid="maintenance-urgency-select" value={form.urgency} onChange={update("urgency")} className={`${inputCls} cursor-pointer`}>
                  {URGENCY.map(([v, label]) => (
                    <option key={v} value={v}>{label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label htmlFor="mx-contact" className={labelCls}>Best way to reach you</label>
                <input id="mx-contact" data-testid="maintenance-contact-input" value={form.contact} onChange={update("contact")} placeholder="Phone or email" className={inputCls} />
              </div>
              <div>
                <label htmlFor="mx-description" className={labelCls}>Describe the issue *</label>
                <textarea id="mx-description" data-testid="maintenance-description-input" required rows={4} value={form.description} onChange={update("description")} placeholder="What happened, where in the home, when it started..." className={`${inputCls} resize-none`} />
              </div>
              <div>
                <label htmlFor="mx-photo" className={labelCls}>Photo (optional)</label>
                <input
                  id="mx-photo"
                  data-testid="maintenance-photo-input"
                  type="file"
                  accept="image/*"
                  onChange={(e) => setPhoto(e.target.files?.[0] || null)}
                  className="block w-full text-sm text-[#16301f]/70 file:mr-4 file:border file:border-[#16301f]/30 file:bg-transparent file:text-[#16301f] file:text-xs file:uppercase file:tracking-[0.2em] file:px-4 file:py-2.5 hover:file:bg-[#16301f]/10 file:transition-colors file:cursor-pointer"
                />
              </div>
              <button
                data-testid="maintenance-submit-btn"
                type="submit"
                disabled={sending}
                className="w-full inline-flex items-center justify-center gap-2 bg-[#16301f] text-[#f7f2e7] text-xs uppercase tracking-[0.25em] px-6 py-4 hover:bg-[#284b35] disabled:opacity-60 transition-colors duration-300"
              >
                <Wrench className="w-4 h-4" strokeWidth={1.5} />
                {sending ? "Sending..." : "Send Request"}
              </button>
            </form>
          )}
        </div>

        <p className="text-center mt-10">
          <a data-testid="maintenance-back-link" href="/" className="text-xs uppercase tracking-[0.22em] text-[#16301f]/50 hover:text-[#16301f] transition-colors duration-300">
            ← Back to website
          </a>
        </p>
      </div>
    </div>
  );
}
